import { c as createFocusTrap } from './focus-trap.esm-MYAhwz8v.js';
import { P as Pubsub } from './pubsub-DKqIEqaC.js';
import './notyf-DIw0RvBj.js';

/**
 * Drawer Usage:
 * Call the open(), close() or toggle() method to toggle the drawer
 *
 *
 * Drawer Toggle controllers:
 * Add these data attributes to any element to make it control a drawer:
 *
 * aria-controls="drawer-id"    // Required: unique ID of the drawer to control
 * aria-expanded="false"        // Required: 'true' or 'false'
 * data-drawer-action="toggle"  // Optional: 'open', 'close', or 'toggle' (default)
 *
 * Example:
 * <button aria-controls="cart-drawer" aria-expanded="false">Open Cart</button>
 */

class DrawerElement extends HTMLElement {
  autoFocusElement = this.querySelector('[autofocus]');

  static {
    // Let the document listen for clicks on drawer controllers
    document.addEventListener('click', (event) => {
      const controller = event.target.closest('[data-drawer-controller]');
      const drawerId = controller?.getAttribute('aria-controls');
      if (!drawerId) return;

      const { drawerAction } = controller.dataset;
      const drawer = document.getElementById(drawerId);
      if (!drawer) {
        console.error(`Drawer with id '${drawerId}' not found`);
        return;
      }
      drawer.handleAction(drawerAction);
      event.preventDefault();
    });
  }

  connectedCallback() {
    // Set the options
    const { subscribeEventName, subscribeEventAction } = this.dataset;
    this.options = { subscribeEventName, subscribeEventAction };

    // Get the modal body
    this.modalBody = this.querySelector('[data-drawer-body]');
    if (!this.modalBody) return;

    // Listen for key:escape events
    this.unsubscribeEscapeKey = Pubsub.subscribe('key:escape', () => this.close());

    // Create the focus trap
    this.trap = createFocusTrap(this.modalBody, {
      // Allow clicks outside the modal to close the drawer
      allowOutsideClick: true,
      initialFocus: this.autoFocusElement || undefined,
    });

    // Activate the trap if the drawer is rendered open
    if (this.ariaHidden === 'false') {
      this.trap.activate();
    }

    // Subscribe to the event
    this.subscribeEvents();

    this.#handleShopifyEvents();
  }

  disconnectedCallback() {
    this.unsubscribeEscapeKey();
  }

  /**
   * Toggle overflow hidden on body to prevent background scrolling
   * @param {boolean} lock - Whether to lock body scrolling
   */
  toggleBodyScroll(lock) {
    document.body.classList.toggle('overflow-hidden', lock);
  }

  /**
   * Handle drawer actions
   * @param {string} action - The action to perform ('open', 'close', or 'toggle')
   */
  handleAction(action) {
    switch (action) {
      case 'open':
        this.open();
        break;
      case 'close':
        this.close();
        break;
      default:
        this.toggle();
        break;
    }
  }

  /**
   * Handle drawer controls
   */
  updateControllers() {
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((element) => {
      element.setAttribute('aria-expanded', this.ariaHidden === 'false' ? 'true' : 'false');
    });
  }

  /**
   * Open the drawer
   */
  open() {
    if (this.ariaHidden === 'false') return;

    // Activate the focus trap after the transition ends
    const handleTransition = (event) => {
      if (event.target !== this.modalBody) return;
      this.trap.activate();
      // Prevent focus on the first element
      document.activeElement?.blur();
      this.modalBody.removeEventListener('transitionend', handleTransition);
    };
    // Only listen for transitionend when opening the drawer
    this.modalBody.addEventListener('transitionend', handleTransition);

    this.ariaHidden = false;
    this.toggleBackdrop(false);
    this.updateControllers();

    this.toggleBodyScroll(true);

    this.publishEvent('drawer:opened');
  }

  /**
   * Close the drawer
   */
  close() {
    if (this.ariaHidden !== 'false') return;

    this.trap.deactivate();
    setTimeout(() => {
      this.ariaHidden = true;
      this.toggleBackdrop(true);
      this.updateControllers();
      this.toggleBodyScroll(false);
      this.publishEvent('drawer:closed');
    }, 100);
  }

  /**
   * Toggle the drawer
   */
  toggle() {
    this.ariaHidden === 'true' ? this.open() : this.close();
  }

  /**
   * Toggle the backdrop
   * @param {boolean} hide - Whether to hide the backdrop
   */
  toggleBackdrop(hide) {
    this.backdrop = this.backdrop || this.querySelector('[data-backdrop]');
    this.backdrop.ariaHidden = hide;
  }

  /**
   * Update the trap container content elements, always add the header and footer.
   * @param {HTMLElement} element - The new element to trap focus on
   */
  updateTrapContainerElements(element) {
    this.drawerHeader = this.drawerHeader || this.querySelector('.drawer__header');
    this.drawerFooter = this.drawerFooter || this.querySelector('.drawer__footer');
    const elementsArray = [this.drawerHeader, element, this.drawerFooter].filter(Boolean); // Remove null/undefined elements

    this.trap.updateContainerElements(elementsArray);

    // Prevent focus on the first element
    document.activeElement?.blur();
  }

  /**
   * Publish the event
   */
  publishEvent(eventName) {
    Pubsub.publish(`${eventName}:${this.id}`, {
      target: this.id,
    });
  }

  /**
   * Subscribe to the event
   */
  subscribeEvents() {
    if (!this.options.subscribeEventName || !this.options.subscribeEventAction) return;

    Pubsub.subscribe(this.options.subscribeEventName, () => {
      this.handleAction(this.options.subscribeEventAction);
    });
  }

  /**
   * Handle block select events
   */
  #handleShopifyEvents() {
    const events = [
      'shopify:section:load',
      'shopify:section:select',
      'shopify:section:unload',
      'shopify:section:deselect',
    ];

    events.forEach((eventName) => {
      document.addEventListener(eventName, (event) => {
        const { target, type } = event;
        const drawer = target.querySelector('drawer-element');

        switch (type) {
          case 'shopify:section:load':
          case 'shopify:section:select':
            drawer?.open();
            break;
          case 'shopify:section:unload':
          case 'shopify:section:deselect':
            drawer?.close();
            break;
        }
      });
    });
  }
}

customElements.define('drawer-element', DrawerElement);

export { DrawerElement };
